// store/slices/airdropSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import airdropService from "../../services/airdropService";

interface AirdropState {
  airdrops: any[];
  userAirdrops: any[];
  isLoading: boolean;
  isParticipating: boolean;
  error: string | null;
}

const initialState: AirdropState = {
  airdrops: [],
  userAirdrops: [],
  isLoading: false,
  isParticipating: false,
  error: null,
};

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const getAirdrops = createAsyncThunk(
  "airdrop/getAirdrops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await airdropService.getAirdrops();
      // response is AxiosResponse; payload is response.data
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch airdrops",
      );
    }
  },
);

export const getUserAirdrops = createAsyncThunk(
  "airdrop/getUserAirdrops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await airdropService.getUserAirdrops();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch your airdrops",
      );
    }
  },
);

export const participateInAirdrop = createAsyncThunk(
  "airdrop/participate",
  async (airdropId: number, { rejectWithValue }) => {
    try {
      const response = await airdropService.participate(airdropId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to participate in airdrop",
      );
    }
  },
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const airdropSlice = createSlice({
  name: "airdrop",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // ── Get Airdrops ───────────────────────────────────────────────────────
      .addCase(getAirdrops.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAirdrops.fulfilled, (state, action) => {
        state.isLoading = false;
        // Laravel paginator wraps rows in .data; plain arrays come through directly
        state.airdrops = action.payload?.data ?? action.payload ?? [];
      })
      .addCase(getAirdrops.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ── Get User Airdrops ──────────────────────────────────────────────────
      .addCase(getUserAirdrops.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserAirdrops.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userAirdrops = action.payload?.data ?? action.payload ?? [];
      })
      .addCase(getUserAirdrops.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ── Participate ────────────────────────────────────────────────────────
      .addCase(participateInAirdrop.pending, (state) => {
        state.isParticipating = true;
        state.error = null;
      })
      .addCase(participateInAirdrop.fulfilled, (state) => {
        state.isParticipating = false;
      })
      .addCase(participateInAirdrop.rejected, (state, action) => {
        state.isParticipating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = airdropSlice.actions;
export default airdropSlice.reducer;
